/**
 * Dialog360 Cloud Templates Tab
 * Manage WhatsApp message templates ON 360dialog. Uses the CCMS data table with a
 * sortable Created column; view/delete happen in the template detail modal.
 */

import { useEffect, useMemo, useState } from 'react';
import { IconRefresh, IconPlus, IconTrash } from '@tabler/icons-react';
import {
  Stack,
  Group,
  Button,
  Text,
  Title,
  Badge,
  Card,
  EmptyState,
  Modal,
  Divider,
  TextInput,
  Alert,
  SimpleGrid,
  ScrollArea,
} from '../../../../../components/ui';
import { CCMSDataTable } from '../../../../../components/ccms-components/data-display/components';
import type { TableColumn } from '../../../../../components/ccms-components/data-display/types';
import { ConfirmDeleteModal } from '../../../../../components/ui/overlays';
import { useDialog360 } from '../hooks/useDialog360';
import type { TemplateListItem, CreateTemplateApiRequest } from '../dialog360.types';
import { templateStatusColor, formatIsoDateTime } from '../dialog360.types';
import { Dialog360TemplateBuilder } from './Dialog360TemplateBuilder';
import { Dialog360TemplatePreview } from './Dialog360TemplatePreview';

export function Dialog360CloudTemplatesTab() {
  const { loading, cloudTemplates, fetchCloudTemplates, createCloudTemplate, deleteCloudTemplate } =
    useDialog360();

  const [createOpened, setCreateOpened] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TemplateListItem | null>(null);
  const [viewTarget, setViewTarget] = useState<TemplateListItem | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ field: string; direction: 'asc' | 'desc' }>({ field: 'createdAt', direction: 'desc' });

  useEffect(() => {
    fetchCloudTemplates();
  }, [fetchCloudTemplates]);

  const handleCreate = async (req: CreateTemplateApiRequest) => {
    await createCloudTemplate(req);
    setCreateOpened(false);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    await deleteCloudTemplate(deleteTarget.name);
    setDeleteTarget(null);
    setViewTarget(null); // close the detail modal too
  };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? cloudTemplates.filter((t) =>
          [t.name, t.category, t.language, t.status].some((v) => String(v || '').toLowerCase().includes(q)))
      : cloudTemplates;
    const { field, direction } = sort;
    const dir = direction === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      let av: any; let bv: any;
      if (field === 'createdAt') { av = a.createdAt ? Date.parse(a.createdAt) : 0; bv = b.createdAt ? Date.parse(b.createdAt) : 0; }
      else { av = String((a as any)[field] ?? '').toLowerCase(); bv = String((b as any)[field] ?? '').toLowerCase(); }
      if (av < bv) return -dir;
      if (av > bv) return dir;
      return 0;
    });
  }, [cloudTemplates, search, sort]);

  const columns: TableColumn<TemplateListItem>[] = [
    { key: 'name', header: 'Name', accessor: 'name', sortable: true, render: (v) => <Text ff="monospace" size="sm">{v}</Text> },
    { key: 'language', header: 'Language', accessor: 'language', sortable: true, render: (v) => <Text size="sm">{v || '-'}</Text> },
    { key: 'category', header: 'Category', accessor: 'category', sortable: true, render: (v) => <Badge variant="light" size="sm">{v || '-'}</Badge> },
    {
      key: 'status',
      header: 'Status',
      accessor: 'status',
      sortable: true,
      render: (v, row) => {
        const status = String(v || '');
        const rejected = status.toLowerCase() === 'rejected' || (!!row.rejectedReason && row.rejectedReason !== 'NONE');
        return rejected ? (
          <span
            style={{ cursor: 'pointer', display: 'inline-flex' }}
            title="View rejection reason"
            onClick={(e) => { e.stopPropagation(); setViewTarget(row); }}
          >
            <Badge variant="filled" color="red" size="sm">{(status || 'REJECTED')} ⓘ</Badge>
          </span>
        ) : (
          <Badge variant="light" color={templateStatusColor(status)} size="sm">{status || 'UNKNOWN'}</Badge>
        );
      },
    },
    { key: 'createdAt', header: 'Created', accessor: 'createdAt', sortable: true, render: (v) => <Text size="sm" c="dimmed">{formatIsoDateTime(v as string)}</Text> },
  ];

  return (
    <Stack gap="md">
      <Card>
        <Group justify="space-between">
          <div>
            <Text fw={500} size="lg">360dialog Templates</Text>
            <Text size="sm" c="dimmed">Manage WhatsApp message templates hosted on 360dialog</Text>
          </div>
          <Group gap="xs">
            <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={() => fetchCloudTemplates()} loading={loading}>
              Refresh
            </Button>
            <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateOpened(true)} gradientVariant="primary">
              Create Template
            </Button>
          </Group>
        </Group>
      </Card>

      <Card>
        {cloudTemplates.length === 0 && !loading ? (
          <EmptyState
            title="No Templates"
            description="No WhatsApp templates are hosted on 360dialog yet. Create one to get started."
            actionLabel="Create Template"
            onAction={() => setCreateOpened(true)}
          />
        ) : (
          <Stack gap="sm">
            <TextInput
              placeholder="Search templates..."
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              w={320}
            />
            <Text size="xs" c="dimmed">{rows.length} template(s). Click a row to view details.</Text>
            <ScrollArea h={520}>
              <CCMSDataTable
                data={rows}
                columns={columns}
                loading={loading}
                sortable
                onSort={(c) => setSort({ field: c.field, direction: c.direction })}
                onRowClick={(row) => setViewTarget(row)}
                emptyMessage="No templates match your search"
              />
            </ScrollArea>
          </Stack>
        )}
      </Card>

      <Dialog360TemplateBuilder
        opened={createOpened}
        onClose={() => setCreateOpened(false)}
        onSubmit={handleCreate}
        submitting={loading}
      />

      <Modal
        opened={viewTarget !== null}
        onClose={() => setViewTarget(null)}
        title={viewTarget?.name}
        size="xxxl"
        styles={{ content: { width: '95vw', maxWidth: 1500 } }}
      >
        {viewTarget && (
          <Stack gap="md">
            {viewTarget.rejectedReason && viewTarget.rejectedReason !== 'NONE' && (
              <Alert color="red" title="Template rejected by Meta">{viewTarget.rejectedReason}</Alert>
            )}
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
              <Stack gap="sm">
                <Group gap="xs">
                  <Badge variant="light" color={templateStatusColor(viewTarget.status)}>{viewTarget.status || 'UNKNOWN'}</Badge>
                  <Badge variant="light">{viewTarget.category || '-'}</Badge>
                  <Badge variant="light" color="gray">{viewTarget.language || '-'}</Badge>
                </Group>

                <Divider label="Details" labelPosition="left" />
                <Group justify="space-between" gap="sm"><Text size="sm" c="dimmed">Name</Text><Text size="sm" ff="monospace">{viewTarget.name}</Text></Group>
                {viewTarget.externalId && <Group justify="space-between" gap="sm"><Text size="sm" c="dimmed">External ID</Text><Text size="sm" ff="monospace">{viewTarget.externalId}</Text></Group>}
                {viewTarget.namespace && <Group justify="space-between" gap="sm"><Text size="sm" c="dimmed">Namespace</Text><Text size="xs" ff="monospace">{viewTarget.namespace}</Text></Group>}
                <Group justify="space-between" gap="sm"><Text size="sm" c="dimmed">Created</Text><Text size="sm">{formatIsoDateTime(viewTarget.createdAt)}</Text></Group>
                {viewTarget.modifiedAt && <Group justify="space-between" gap="sm"><Text size="sm" c="dimmed">Modified</Text><Text size="sm">{formatIsoDateTime(viewTarget.modifiedAt)}</Text></Group>}

                <Divider label="Components" labelPosition="left" />
                {(viewTarget.components || []).map((c, i) => (
                  <Group key={i} gap="xs" align="flex-start" wrap="nowrap">
                    <Badge size="xs" variant="light" style={{ flexShrink: 0 }}>{c.type}{c.format ? ` · ${c.format}` : ''}</Badge>
                    <Text size="xs" c="dimmed" style={{ flex: 1, whiteSpace: 'pre-wrap' }}>
                      {c.text || (c.buttons ? c.buttons.map((b) => b.text).join(', ') : (c.example?.headerHandle?.[0] ? 'media sample attached' : ''))}
                    </Text>
                  </Group>
                ))}
              </Stack>

              <Stack gap="xs">
                <Title order={6}>How it will look</Title>
                <Dialog360TemplatePreview template={viewTarget} />
              </Stack>
            </SimpleGrid>

            <Divider />
            <Group justify="flex-end">
              <Button color="red" variant="light" leftSection={<IconTrash size={16} />} onClick={() => { setDeleteTarget(viewTarget); setViewTarget(null); }}>
                Delete template
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <ConfirmDeleteModal
        opened={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Template"
        message="Are you sure you want to delete this template from 360dialog?"
        itemName={deleteTarget?.name}
        loading={loading}
      />
    </Stack>
  );
}
